import * as XLSX from '@e965/xlsx';
import type { ChartVisualizationContent } from '../AskAsperAgentic.types';
import type { VizDataFormat } from '../../../molecules/DataTable/DataTable';

function isTableViz(chartType?: string, vizData?: { chart_type?: string }): boolean {
    const t = String(chartType || vizData?.chart_type || '').toLowerCase();
    return t === 'table';
}

/**
 * Returns the first table visualization payload from chart artefact content (the same structure
 * rendered by DataTable), or null if there is no table.
 */
export function extractTableVizFromChartVisualizationContent(
    content: ChartVisualizationContent | undefined,
): VizDataFormat | null {
    if (!content || content.type !== 'chartVisualization') {
        return null;
    }

    if (content.visualizations?.length) {
        for (const item of content.visualizations) {
            if (
                isTableViz(item.chartType, item.vizData) &&
                Array.isArray(item.vizData?.data) &&
                item.vizData.data.length > 0
            ) {
                return item.vizData as VizDataFormat;
            }
        }
        return null;
    }

    if (
        content.vizData &&
        isTableViz(content.chartType, content.vizData) &&
        Array.isArray(content.vizData.data) &&
        content.vizData.data.length > 0
    ) {
        return content.vizData as VizDataFormat;
    }

    return null;
}

function normalizeCellForXlsx(value: unknown): string | number | boolean {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    let s = String(value);
    if (/<[a-z][\s\S]*>/i.test(s)) {
        s = s.replace(/<[^>]+>/g, '').trim();
    }
    return s;
}

function vizDataToAoA(viz: VizDataFormat): (string | number | boolean)[][] {
    const grid = viz.data;
    if (!Array.isArray(grid) || grid.length === 0) {
        throw new Error('No table data to export');
    }
    return grid.map((row) => {
        if (!Array.isArray(row)) {
            return [normalizeCellForXlsx(row)];
        }
        return row.map((cell) => normalizeCellForXlsx(cell));
    });
}

/** Excel sheet names: max 31 chars; cannot contain [] * ? : \ / */
function uniqueSheetName(title: string, index: number, used: Set<string>): string {
    const raw = title
        .replace(/[[\]*?:/\\]/g, '_')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 31);
    let base = raw || `Sheet${index + 1}`;
    let name = base.slice(0, 31);
    let n = 2;
    while (used.has(name)) {
        const suffix = `_${n}`;
        name = (base.slice(0, 31 - suffix.length) + suffix).slice(0, 31);
        n += 1;
    }
    used.add(name);
    return name;
}

/**
 * Build an .xlsx from the 2D grid used by {@link DataTable} (header row + body rows).
 * This reflects the visualization payload available to the client, not the full server-side blob.
 */
export function exportAgenticTableVizToExcel(viz: VizDataFormat, filenameBase?: string): void {
    const rows = vizDataToAoA(viz);
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const base =
        (filenameBase || viz.chartTitle || 'table').replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'table';
    XLSX.writeFile(workbook, `${base}_${Date.now()}.xlsx`);
}

export interface ExportTableVizSheetSpec {
    viz: VizDataFormat;
    /** Used for the worksheet tab name */
    title: string;
}

/**
 * One workbook with a sheet per table (e.g. multi-select "current view" export).
 */
export function exportMultipleAgenticTableVizToExcel(sheets: ExportTableVizSheetSpec[], filenameBase?: string): void {
    if (sheets.length === 0) {
        throw new Error('No table data to export');
    }
    const workbook = XLSX.utils.book_new();
    const usedNames = new Set<string>();
    sheets.forEach((spec, index) => {
        const rows = vizDataToAoA(spec.viz);
        const worksheet = XLSX.utils.aoa_to_sheet(rows);
        const sheetName = uniqueSheetName(spec.title, index, usedNames);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });
    const base =
        (filenameBase || 'selected_artefacts').replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'selected_artefacts';
    XLSX.writeFile(workbook, `${base}_${Date.now()}.xlsx`);
}
